import { transparentize } from 'polished';
import styled, { css } from 'styled-components';
import { Box, HStack } from '../Core';

export const IndicatorBadgeVariants = {
  Default: 'DEFAULT',
  Primary: 'PRIMARY',
  Notification: 'NOTIFICATION',
  Positive: 'POSITIVE',
  Negative: 'NEGATIVE',
  Information: 'INFORMATION',
  Modified: 'MODIFIED',
  Warning: 'WARNING',
} as const;
export type IndicatorBadgeVariants = (typeof IndicatorBadgeVariants)[keyof typeof IndicatorBadgeVariants];

export const IndicatorBadgeSizes = {
  /** Same typography as Small; tighter horizontal padding (3px) for single-glyph badges. */
  Compact: 'Compact',
  Small: 'Small',
  Large: 'Large',
} as const;
export type IndicatorBadgeSizes = (typeof IndicatorBadgeSizes)[keyof typeof IndicatorBadgeSizes];

export const Wrapper = styled(HStack)`
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing4}px;
  cursor: default;
`;

export const IndicatorBadgeWrapper = styled(Box)<{
  variant: IndicatorBadgeVariants;
  size: IndicatorBadgeSizes;
  square?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  text-align: center;

  ${({ size, theme, square }) =>
    size === IndicatorBadgeSizes.Compact &&
    css`
      padding: ${theme.spacing2}px ${theme.spacing2 + 1}px;
      font-size: ${theme.fontSizeTiny}rem;
      line-height: ${theme.lineHeightTiny}rem;
      ${square &&
      css`
        height: 14px;
        width: 14px;
      `}
    `}
  ${({ size, theme, square }) =>
    size === IndicatorBadgeSizes.Small &&
    css`
      padding: ${theme.spacing2}px ${theme.spacing4 + 1}px;
      font-size: ${theme.fontSizeTiny}rem;
      line-height: ${theme.lineHeightTiny}rem;
      ${square &&
      css`
        height: 14px;
        width: 14px;
      `}
    `}
  ${({ size, theme, square }) =>
    size === IndicatorBadgeSizes.Large &&
    css`
      padding: ${theme.spacing2}px ${theme.spacing8 - 1}px;
      font-size: ${theme.fontSizeSmall}rem;
      line-height: ${theme.lineHeightSmall}rem;
      ${square &&
      css`
        height: 18px;
        width: 18px;
      `}
    `}

  ${({ variant, theme }) =>
    variant === IndicatorBadgeVariants.Default &&
    css`
      background: ${transparentize(0.92, theme.colors.gray['100'])};
      color: ${theme.colors.gray['090']};
    `};
  ${({ variant, theme }) =>
    variant === IndicatorBadgeVariants.Primary &&
    css`
      background: ${theme.colors.primary.default};
      color: ${theme.colorTextPrimaryButton};
    `};
  ${({ variant, theme }) =>
    variant === IndicatorBadgeVariants.Notification &&
    css`
      background: ${theme.colors.orange.default};
      color: ${theme.colors.white.default};
    `}
  ${({ variant, theme }) =>
    variant === IndicatorBadgeVariants.Negative &&
    css`
      background: ${transparentize(0.72, theme.colors.red.default)};
      color: ${theme.colors.red.lighten};
    `}

  ${({ variant, theme }) =>
    variant === IndicatorBadgeVariants.Positive &&
    css`
      background: ${transparentize(0.72, theme.colors.green.default)};
      color: ${theme.colors.green.lighten};
    `}

  ${({ variant, theme }) =>
    variant === IndicatorBadgeVariants.Information &&
    css`
      background: ${transparentize(0.72, theme.colors.blue.default)};
      color: ${theme.colors.blue.lighten};
    `}
    
  ${({ variant, theme }) =>
    variant === IndicatorBadgeVariants.Modified &&
    css`
      background: ${theme.colors.blue.default};
      color: ${theme.colors.white.default};
      border: 1px solid ${theme.colors.blue.lighten};
    `}

  ${({ variant, theme }) =>
    variant === IndicatorBadgeVariants.Warning &&
    css`
      background: ${transparentize(0.72, theme.colors.yellow.default)};
      color: ${theme.colors.yellow.lighten};
    `}
`;
